import { additionalOccupations, getOccupationDetail, occupationFamilies } from "./occupations.ts";

export type AnswerOption = { code: string; label: string };
export type SurveyQuestion = {
  id: "workStatus" | "industry" | "occupation" | "goal" | "action" | "evidence" | "recovery" | "useCase" | "blocker" | "advanced";
  eyebrow: string;
  title: string;
  hint: string;
  options: AnswerOption[];
  allowOther?: boolean;
};

export type SurveyAnswers = {
  workStatus?: string;
  industry?: string;
  occupation?: string;
  occupationDetail?: string;
  occupationOther?: string;
  goal: string;
  action: string;
  evidence: string;
  recovery: string;
  useCase: string;
  useCaseOther?: string;
  blocker: string;
  blockerOther?: string;
  advanced?: string;
};

export type Course = {
  id: string;
  classNumber: number;
  title: string;
  group: string;
  problem: string;
  outcome: string;
};

export type SurveyResult = {
  recommendedCourseId: string;
  recommendedCourseTitle: string;
  targetSummary: string;
  currentSummary: string;
  nextStep: string;
  roadmap: string[];
  needsReview: boolean;
  reasonCodes: string[];
};

export const SURVEY_VERSION = "survey-v5";
export const RULE_VERSION = "rules-v1";
export const CONSENT_VERSION = "consent-v2";

export const questions: SurveyQuestion[] = [
  {
    id: "workStatus",
    eyebrow: "Đôi nét về bạn",
    title: "Hiện tại bạn đang học tập hoặc làm việc theo hình thức nào?",
    hint: "Chọn một phương án phù hợp nhất để FUNiX gợi ý bài tập và ví dụ sát với bạn.",
    options: [
      { code: "STUDENT", label: "Sinh viên, chưa đi làm" },
      { code: "STUDENT_WORKING", label: "Sinh viên, đang đi làm hoặc thực tập" },
      { code: "EMPLOYEE", label: "Đang đi làm — nhân viên/chuyên viên" },
      { code: "MANAGER", label: "Đang đi làm — quản lý/lãnh đạo" },
      { code: "SELF_EMPLOYED", label: "Làm tự do hoặc tự kinh doanh" },
      { code: "JOB_SEEKER", label: "Hiện chưa đi làm / Đang tìm việc" },
    ],
  },
  {
    id: "industry",
    eyebrow: "Ngành bạn đang làm việc",
    title: "Doanh nghiệp hoặc tổ chức bạn đang làm việc thuộc ngành nào?",
    hint: "Chọn một ngành chính. Ví dụ: làm kế toán tại công ty bán lẻ thì chọn Bán lẻ / Thương mại điện tử. Nếu làm tự do, chọn ngành khách hàng bạn phục vụ nhiều nhất.",
    options: [
      { code: "I01", label: "Công nghệ" },
      { code: "I02", label: "Sản xuất" },
      { code: "I03", label: "Tài chính / Ngân hàng / Bảo hiểm" },
      { code: "I04", label: "Logistics / Vận tải" },
      { code: "I05", label: "Năng lượng" },
      { code: "I06", label: "Xây dựng" },
      { code: "I07", label: "Bán lẻ / Thương mại điện tử" },
      { code: "I08", label: "Y tế / Dược" },
      { code: "I09", label: "Du lịch / Khách sạn" },
      { code: "I10", label: "Nông nghiệp" },
      { code: "I11", label: "Bất động sản" },
      { code: "I12", label: "Giáo dục / Đào tạo" },
      { code: "I13", label: "Truyền thông / Quảng cáo" },
      { code: "OTHER", label: "Ngành khác" },
      { code: "NOT_WORKING", label: "Chưa đi làm / Hiện chưa làm việc" },
    ],
  },
  {
    id: "occupation",
    eyebrow: "Công việc chính của bạn",
    title: "Bạn làm nghề hoặc công việc gì?",
    hint: "Chọn công việc chính của bạn. Ví dụ: kế toán tại công ty bán lẻ → nghề Kế toán.",
    options: [
      { code: "O01", label: "AI / Machine Learning" },
      { code: "O18", label: "Dữ liệu / BI / Phân tích dữ liệu" },
      { code: "O19", label: "An ninh mạng / Cyber Security" },
      { code: "O02", label: "Lập trình / Phát triển phần mềm" },
      { code: "O20", label: "Kiểm thử phần mềm / QA / QC" },
      { code: "O21", label: "Hạ tầng IT / Hệ thống / Mạng / Helpdesk" },
      { code: "O22", label: "Sản phẩm / Business Analyst / Quản lý dự án" },
      { code: "O03", label: "Cơ khí / Điện / Điện tử" },
      { code: "O23", label: "Tự động hóa / Robot / Điều khiển" },
      { code: "O24", label: "Bảo trì / Hỗ trợ kỹ thuật" },
      { code: "O04", label: "Vận hành / Quy trình" },
      { code: "O25", label: "Chuỗi cung ứng / Thu mua" },
      { code: "O26", label: "Logistics / Xuất nhập khẩu" },
      { code: "O27", label: "Kho vận / Giao nhận" },
      { code: "O28", label: "Sản xuất / Kế hoạch sản xuất" },
      { code: "O29", label: "Quản lý chất lượng / QA / QC sản xuất" },
      { code: "O05", label: "Kế toán" },
      { code: "O14", label: "Tài chính" },
      { code: "O16", label: "Ngân hàng" },
      { code: "O17", label: "Bảo hiểm" },
      { code: "O15", label: "Pháp chế / Luật" },
      { code: "O06", label: "Ngoại ngữ / Biên phiên dịch" },
      { code: "O07", label: "UI / UX / Thiết kế sản phẩm" },
      { code: "O30", label: "Thiết kế đồ họa / Media / Sáng tạo" },
      { code: "O08", label: "Nhân sự / Tuyển dụng / C&B" },
      { code: "O31", label: "Hành chính / Văn phòng / Trợ lý" },
      { code: "O09", label: "Marketing / Growth / Quảng cáo" },
      { code: "O32", label: "Content / Social Media / PR" },
      { code: "O10", label: "Kinh doanh / Phát triển kinh doanh" },
      { code: "O33", label: "Bán lẻ / Thương mại điện tử" },
      { code: "O11", label: "Chăm sóc khách hàng" },
      { code: "O12", label: "Quản lý / Lãnh đạo" },
      { code: "O13", label: "Giảng dạy / Đào tạo / Phát triển học liệu" },
      { code: "O34", label: "Y tế / Dược / Chăm sóc sức khỏe" },
      { code: "O35", label: "Xây dựng / Kiến trúc / Nội thất" },
      { code: "O36", label: "Du lịch / Nhà hàng / Khách sạn" },
      ...additionalOccupations,
      { code: "OTHER", label: "Nhóm công việc khác" },
      { code: "UNDECIDED", label: "Chưa xác định nghề muốn theo đuổi" },
    ],
  },
  {
    id: "goal",
    eyebrow: "Đích muốn tới",
    title: "Bạn muốn AI giúp mình đạt được điều gì nhất?",
    hint: "Chọn mục tiêu bạn muốn hướng tới, kể cả khi hiện tại chưa biết bắt đầu từ đâu.",
    options: [
      { code: "G1", label: "Dùng AI tốt hơn trong công việc hằng ngày" },
      { code: "G2", label: "Giảm các công việc lặp lại bằng tự động hóa" },
      { code: "G3", label: "Xây dựng một AI Agent làm được một việc cụ thể" },
      { code: "G4", label: "Kết nối nhiều Agent hoặc quy trình với nhau" },
      { code: "G5", label: "Triển khai AI cho team hoặc doanh nghiệp" },
    ],
  },
  {
    id: "action",
    eyebrow: "Việc đã làm thật",
    title: "Hiện tại, bạn đã thực sự làm được tới đâu?",
    hint: "Chọn phương án cao nhất bạn đã tự thực hiện, không chọn theo điều đang muốn học.",
    options: [
      { code: "A0", label: "Chưa sử dụng AI hoặc mới thử ChatGPT/Claude một vài lần" },
      { code: "A1", label: "Đã sử dụng AI thường xuyên cho một số đầu việc" },
      { code: "A2", label: "Đã có prompt, hướng dẫn hoặc bộ mẫu có thể dùng lại" },
      { code: "A3", label: "Đã tự động hóa được ít nhất một quy trình" },
      { code: "A4", label: "Đã tạo được một Agent có thể xử lý một công việc" },
      { code: "A5", label: "Đã kết nối Agent với dữ liệu hoặc công cụ thực tế" },
      { code: "A6", label: "Đã vận hành nhiều Agent và có cách kiểm soát kết quả" },
    ],
  },
  {
    id: "evidence",
    eyebrow: "Sản phẩm đang có",
    title: "Bạn đang có sản phẩm hoặc bằng chứng nào dưới đây?",
    hint: "Chọn sản phẩm cao nhất bạn đang thực sự sử dụng hoặc có thể mở ra kiểm tra.",
    options: [
      { code: "E0", label: "Chưa có sản phẩm cụ thể" },
      { code: "E1", label: "Một số prompt hoặc bộ mẫu đang sử dụng" },
      { code: "E2", label: "Một workflow xử lý công việc đã được chuẩn hóa" },
      { code: "E3", label: "Một automation đang chạy thực tế" },
      { code: "E4", label: "Một Agent đang xử lý một công việc thực tế" },
      { code: "E5", label: "Một Agent đã kết nối với hệ thống hoặc dữ liệu" },
      { code: "E6", label: "Một hệ nhiều Agent có log, tiêu chí đánh giá và cách xử lý lỗi" },
    ],
  },
  {
    id: "recovery",
    eyebrow: "Cách bạn xử lý",
    title: "Khi AI cho kết quả chưa đúng, bạn thường xử lý như thế nào?",
    hint: "Chọn cách gần nhất với điều bạn đang thực hiện.",
    options: [
      { code: "D0", label: "Không biết cần sửa ở đâu nên thường tự làm lại" },
      { code: "D1", label: "Viết lại câu lệnh hoặc thử một prompt khác" },
      { code: "D2", label: "Bổ sung bối cảnh, dữ liệu và tiêu chí đầu ra" },
      { code: "D3", label: "Kiểm tra từng bước trong workflow hoặc automation" },
      { code: "D4", label: "Xem log, xác định bước lỗi và điều chỉnh cách Agent xử lý" },
      { code: "D5", label: "Có bộ tình huống kiểm thử và tiêu chí đánh giá trước khi cập nhật" },
    ],
  },
  {
    id: "useCase",
    eyebrow: "Bài toán ưu tiên",
    title: "Công việc nào bạn muốn ứng dụng AI trước tiên?",
    hint: "Chọn một việc cụ thể để định hướng sát với công việc của bạn.",
    options: [
      { code: "U1", label: "Viết email, nội dung hoặc tài liệu" },
      { code: "U2", label: "Tìm kiếm, đọc và tổng hợp thông tin" },
      { code: "U3", label: "Phân tích dữ liệu hoặc làm báo cáo" },
      { code: "U4", label: "Chuẩn hóa một công việc lặp lại" },
      { code: "U5", label: "Tự động hóa nhập liệu, chuyển dữ liệu hoặc nhắc việc" },
      { code: "U6", label: "Xây dựng trợ lý/Agent cho một nhiệm vụ cụ thể" },
      { code: "U7", label: "Kết nối nhiều công cụ, dữ liệu hoặc Agent" },
      { code: "U8", label: "Triển khai AI cho team/doanh nghiệp" },
      { code: "OTHER", label: "Khác" },
    ],
    allowOther: true,
  },
  {
    id: "blocker",
    eyebrow: "Điểm đang vướng",
    title: "Điều gì đang khiến bạn khó tiến tới mục tiêu nhất?",
    hint: "Câu trả lời giúp FUNiX gợi ý bước tiếp theo thiết thực hơn.",
    options: [
      { code: "B1", label: "Dùng AI nhưng kết quả vẫn phải sửa nhiều" },
      { code: "B2", label: "Có quá nhiều công cụ, không biết nên học gì" },
      { code: "B3", label: "Biết cách dùng AI nhưng chưa đưa được vào công việc" },
      { code: "B4", label: "Chưa biết biến công việc thành một quy trình rõ ràng" },
      { code: "B5", label: "Chưa biết cách tự động hóa hoặc xây Agent" },
      { code: "B6", label: "Đã có Agent nhưng chưa biết kết nối và vận hành ổn định" },
      { code: "B7", label: "Thiếu người hướng dẫn trên bài toán thực tế" },
      { code: "OTHER", label: "Khác" },
    ],
    allowOther: true,
  },
];

export const advancedQuestion: SurveyQuestion = {
  id: "advanced",
  eyebrow: "Xác minh chặng nâng cao",
  title: "Bạn đang có thêm bằng chứng nào dưới đây?",
  hint: "Câu này chỉ xuất hiện khi nền tảng của bạn đã ở chặng nhiều Agent.",
  options: [
    { code: "V0", label: "Chưa có thêm bằng chứng" },
    { code: "V1", label: "Hệ nhiều Agent có bộ kiểm thử và số liệu chất lượng, thời gian, chi phí" },
    { code: "V2", label: "Đã triển khai cho team với người chịu trách nhiệm, phân quyền và xử lý sự cố" },
    { code: "V3", label: "Có dự án end-to-end với người dùng thật, số đo trước–sau, nghiệm thu và bàn giao" },
  ],
};

export const courses: Course[] = [
  { id: "L1", classNumber: 0, group: "Bắt đầu đúng với AI", title: "Bạn có nhu cầu học AI", problem: "Bạn muốn theo kịp AI nhưng chưa chỉ ra được một việc cụ thể đã tự làm bằng AI.", outcome: "Chọn đúng một công việc đang mất thời gian và có mốc để đo kết quả trước–sau." },
  { id: "L2", classNumber: 1, group: "Bắt đầu đúng với AI", title: "GenAI nền tảng & giao việc chất lượng", problem: "AI thường trả lời chung chung, sai ý hoặc khiến bạn phải sửa gần hết.", outcome: "Giao việc rõ ràng, bổ sung bối cảnh và kiểm tra thông tin trước khi sử dụng." },
  { id: "L3", classNumber: 2, group: "Bắt đầu đúng với AI", title: "AI Co-worker cho công việc", problem: "AI giúp từng việc nhưng chưa giảm rõ tổng thời gian và mỗi lần vẫn phải giải thích lại.", outcome: "Tạo bộ trợ lý, nguồn tham chiếu và mẫu đầu ra dùng lại cho công việc hằng ngày." },
  { id: "L4", classNumber: 3, group: "Chuẩn hóa & tự động hóa", title: "Phân tích nghiệp vụ & quy trình", problem: "Bạn dùng được nhiều công cụ nhưng chưa mô tả rõ quy trình và điểm nghẽn cần giải quyết.", outcome: "Chọn đúng bài toán và vẽ rõ bước cần bỏ, chuẩn hóa hoặc giao cho AI." },
  { id: "L5", classNumber: 4, group: "Chuẩn hóa & tự động hóa", title: "Tự động hóa", problem: "Quy trình đã rõ nhưng vẫn phải chuyển dữ liệu, tệp và nhắc việc thủ công.", outcome: "Ghép được quy trình có quy tắc, trạng thái, chống trùng và xử lý khi một bước lỗi." },
  { id: "L6", classNumber: 5, group: "Chuẩn hóa & tự động hóa", title: "Thiết kế Agent đầu tiên", problem: "Bạn muốn giao trọn một việc cho AI nhưng chưa biết giới hạn và cách xử lý thiếu dữ liệu.", outcome: "Tạo Agent cho một việc hẹp, biết hỏi lại, chọn bước và dừng khi vượt phạm vi." },
  { id: "L7", classNumber: 6, group: "Chuẩn hóa & tự động hóa", title: "Knowledge · Memory · Skill", problem: "Agent đã dùng được nhưng trả lời thiếu ổn định hoặc dùng sai phiên bản tài liệu.", outcome: "Tổ chức tri thức, trí nhớ và Skill có phiên bản cùng các ca kiểm tra ổn định." },
  { id: "L8", classNumber: 7, group: "Chuẩn hóa & tự động hóa", title: "Kết nối và hệ thống hóa các Agent của bạn", problem: "Agent làm được việc nhưng dữ liệu vẫn phải đưa vào và cập nhật thủ công.", outcome: "Kết nối Agent với hệ thống đúng phạm vi quyền và có lịch sử thao tác để truy vết." },
  { id: "L9", classNumber: 8, group: "Vận hành hệ Agent", title: "Agent Workflow", problem: "Chuỗi công việc dài vẫn cần người đứng canh và khó tiếp tục đúng chỗ khi có lỗi.", outcome: "Thiết kế luồng có trạng thái, điểm duyệt và đường xử lý ngoại lệ." },
  { id: "L10", classNumber: 9, group: "Vận hành hệ Agent", title: "Điều phối Multi-Agent", problem: "Nhiều vai trò AI dễ dùng khác dữ liệu, bàn giao thiếu và tạo kết quả mâu thuẫn.", outcome: "Tách vai trò đúng lúc, chuẩn hóa bàn giao và điều phối khi các Agent bất đồng." },
  { id: "L11", classNumber: 10, group: "Vận hành hệ Agent", title: "Đánh giá & vận hành Multi-Agent", problem: "Demo chạy tốt nhưng khi dùng nhiều lần chất lượng, thời gian và chi phí thiếu ổn định.", outcome: "Đo chất lượng, lỗi, thời gian và chi phí; kiểm thử thay đổi và có khả năng rollback." },
  { id: "L12", classNumber: 11, group: "Mở rộng trong doanh nghiệp", title: "AI Agent người đồng hành & mở rộng", problem: "Một vài người làm được nhưng team thiếu người sở hữu, phân quyền và cách bàn giao.", outcome: "Quản trị Agent với chủ sở hữu, quyền, phiên bản phê duyệt và cơ chế hỗ trợ rõ ràng." },
  { id: "L13", classNumber: 12, group: "Mở rộng trong doanh nghiệp", title: "Capstone AI Agent Master", problem: "Bạn có nhiều thành phần nhưng chưa chứng minh một dự án trọn vẹn tạo ra giá trị thực.", outcome: "Hoàn thành dự án có người dùng, số đo trước–sau, nghiệm thu và bàn giao vận hành." },
];

const optionLabel = (question: SurveyQuestion, code?: string) =>
  question.options.find((item) => item.code === code)?.label ?? "Chưa xác định";

export function needsAdvancedQuestion(answers: Partial<SurveyAnswers>): boolean {
  return answers.action === "A6" || answers.evidence === "E6";
}

export const studyOptions: AnswerOption[] = [
  { code: "S01", label: "Công nghệ thông tin / AI / Dữ liệu" },
  { code: "S02", label: "Kỹ thuật / Công nghệ / Tự động hóa" },
  { code: "S03", label: "Kinh tế / Quản trị / Kinh doanh / Marketing" },
  { code: "S04", label: "Tài chính / Ngân hàng / Kế toán" },
  { code: "S05", label: "Logistics / Chuỗi cung ứng" },
  { code: "S06", label: "Y / Dược / Sức khỏe" },
  { code: "S07", label: "Sư phạm / Giáo dục" },
  { code: "S08", label: "Ngoại ngữ / Xã hội / Nhân văn" },
  { code: "S09", label: "Thiết kế / Nghệ thuật / Truyền thông" },
  { code: "S10", label: "Luật" },
  { code: "S11", label: "Du lịch / Khách sạn" },
  { code: "S12", label: "Nông nghiệp / Môi trường" },
  { code: "S13", label: "Xây dựng / Kiến trúc" },
  { code: "OTHER", label: "Nhóm ngành học khác" },
];

export function contextualQuestions(answers: Partial<SurveyAnswers>): SurveyQuestion[] {
  return questions.map(q => {
    if (q.id === "industry" && answers.workStatus === "STUDENT") return { ...q, eyebrow: "Ngành học của bạn", title: "Bạn đang học nhóm ngành nào?", hint: "Chọn nhóm gần nhất với ngành học hiện tại của bạn.", options: studyOptions };
    if (q.id === "industry" && answers.workStatus === "JOB_SEEKER") return { ...q, eyebrow: "Ngành muốn theo đuổi", title: "Bạn muốn làm việc trong ngành nào?", hint: "Chọn ngành bạn muốn ứng dụng AI khi tìm việc hoặc chuyển nghề.", options: q.options.filter(o => o.code !== "NOT_WORKING").concat({ code: "UNDECIDED", label: "Chưa xác định ngành muốn theo đuổi" }) };
    if (q.id === "occupation" && ["STUDENT", "JOB_SEEKER"].includes(answers.workStatus ?? "")) return { ...q, eyebrow: "Công việc muốn theo đuổi", title: "Bạn muốn làm nhóm công việc nào?", hint: "Tìm nghề bạn muốn theo đuổi hoặc mở nhóm để chọn. Nếu chưa rõ, chọn Chưa xác định nghề." };
    return q;
  });
}

export function getAnswerLabel(id: SurveyQuestion["id"], code?: string): string {
  if (id === "industry" && code?.startsWith("S")) return studyOptions.find(o => o.code === code)?.label ?? "Chưa xác định";
  if (id === "industry" && code === "UNDECIDED") return "Chưa xác định ngành muốn theo đuổi";
  const question = id === "advanced" ? advancedQuestion : questions.find((item) => item.id === id);
  return question ? optionLabel(question, code) : "Chưa xác định";
}

export function getOccupationMetadata(answers: Partial<SurveyAnswers>) {
  const detail = getOccupationDetail(answers.occupationDetail);
  const family = occupationFamilies.find(item => item.occupations.includes(answers.occupation ?? ""));
  return {
    occupationLabel: getAnswerLabel("occupation", answers.occupation),
    occupationFamilyCode: family?.code ?? "",
    occupationFamilyLabel: family?.label ?? "",
    occupationDetailLabel: detail && detail.occupation === answers.occupation ? detail.label : "",
    occupationOther: answers.occupation === "OTHER" ? String(answers.occupationOther ?? "").trim().slice(0, 120) : "",
  };
}

export function getOccupationText(answers: Partial<SurveyAnswers>): string {
  const metadata = getOccupationMetadata(answers);
  if (metadata.occupationOther) return `Nghề khác: ${metadata.occupationOther}`;
  if (metadata.occupationDetailLabel) return `${metadata.occupationDetailLabel} — ${metadata.occupationLabel}`;
  return metadata.occupationLabel;
}

function numberFromCode(code: string | undefined): number {
  return Number(code?.slice(1) ?? 0);
}

function courseById(id: string): Course {
  const course = courses.find((item) => item.id === id);
  if (!course) throw new Error(`Unknown course: ${id}`);
  return course;
}

const goalTargets: Record<string, { course: string; summary: string }> = {
  G1: { course: "L3", summary: "Dùng AI thành thạo và nhất quán trong công việc hằng ngày." },
  G2: { course: "L5", summary: "Tự động hóa các việc lặp lại để giảm thao tác thủ công." },
  G3: { course: "L7", summary: "Xây dựng một AI Agent làm được việc cụ thể và hoạt động ổn định." },
  G4: { course: "L11", summary: "Kết nối và vận hành nhiều Agent hoặc quy trình với nhau." },
  G5: { course: "L12", summary: "Triển khai AI có quản trị cho team hoặc doanh nghiệp." },
};

export function calculateResult(answers: SurveyAnswers): SurveyResult {
  const a = numberFromCode(answers.action);
  const d = numberFromCode(answers.recovery);
  let id = "L1";
  let needsReview = false;
  const reasons: string[] = [`evidence:${answers.evidence}`, `action:${answers.action}`, `recovery:${answers.recovery}`];

  switch (answers.evidence) {
    case "E0": id = a === 0 || d === 0 ? "L1" : "L2"; break;
    case "E1":
      if (a === 0 || d === 0) { id = "L2"; needsReview = true; } else id = "L3";
      break;
    case "E2":
      if (a < 2 || d < 2) { id = "L3"; needsReview = true; } else id = "L4";
      break;
    case "E3":
      if (a < 3 || d < 3) { id = "L4"; needsReview = true; } else id = "L5";
      break;
    case "E4":
      if (a < 4 || d < 3) { id = "L5"; needsReview = true; }
      else id = d === 3 ? "L6" : "L7";
      break;
    case "E5":
      if (a < 5 || d < 3) { id = "L7"; needsReview = true; }
      else id = d === 5 ? "L9" : "L8";
      break;
    case "E6":
      if (a < 6 || d < 4) { id = "L9"; needsReview = true; }
      else if (d === 4) id = "L10";
      else if (answers.advanced === "V3") id = "L13";
      else if (answers.advanced === "V2") id = "L12";
      else id = "L11";
      break;
    default: throw new Error("Invalid evidence answer");
  }

  const course = courseById(id);
  const target = goalTargets[answers.goal] ?? goalTargets.G1;
  const targetCourse = courseById(target.course);
  const startIndex = courses.findIndex((item) => item.id === id);
  const targetIndex = courses.findIndex((item) => item.id === target.course);
  let roadmap: string[];
  if (startIndex >= targetIndex) roadmap = [course.title, `Áp dụng trực tiếp: ${getAnswerLabel("useCase", answers.useCase)}`];
  else {
    const milestones = courses.slice(startIndex, targetIndex + 1).filter((_, index, all) => index === 0 || index === all.length - 1 || index % 2 === 0);
    roadmap = milestones.map((item) => item.title);
  }

  const blocker = answers.blocker === "OTHER" ? answers.blockerOther : getAnswerLabel("blocker", answers.blocker);
  const useCase = answers.useCase === "OTHER" ? answers.useCaseOther : getAnswerLabel("useCase", answers.useCase);
  const currentSummary = `${getAnswerLabel("action", answers.action)}. ${getAnswerLabel("evidence", answers.evidence)}.`;
  const nextStep = `${course.outcome} Ưu tiên áp dụng vào: ${useCase || "một công việc cụ thể"}. Điểm cần tháo gỡ: ${blocker || "chưa xác định"}.`;
  if (needsReview) reasons.push("signals:inconsistent");
  if (answers.advanced) reasons.push(`advanced:${answers.advanced}`);

  return {
    recommendedCourseId: id,
    recommendedCourseTitle: `Lớp ${course.classNumber} — ${course.title}`,
    targetSummary: target.summary,
    currentSummary,
    nextStep,
    roadmap,
    needsReview,
    reasonCodes: reasons,
  };
}

export function isValidAnswers(value: unknown): value is SurveyAnswers {
  if (!value || typeof value !== "object") return false;
  const answers = value as Record<string, unknown>;
  const checks: Array<[SurveyQuestion, string]> = contextualQuestions(value as SurveyAnswers).map((q) => [q, String(answers[q.id] ?? "")]);
  if (!checks.every(([q, code]) => q.options.some((option) => option.code === code))) return false;
  if (answers.occupationDetail !== undefined && answers.occupationDetail !== "") {
    if (typeof answers.occupationDetail !== "string") return false;
    const detail = getOccupationDetail(answers.occupationDetail);
    if (!detail || detail.occupation !== answers.occupation) return false;
  }
  if (answers.occupationOther !== undefined) {
    if (typeof answers.occupationOther !== "string" || answers.occupationOther.length > 120) return false;
    if (answers.occupation !== "OTHER" && answers.occupationOther.trim()) return false;
  }
  if (answers.useCase === "OTHER" && !String(answers.useCaseOther ?? "").trim()) return false;
  if (answers.blocker === "OTHER" && !String(answers.blockerOther ?? "").trim()) return false;
  if (needsAdvancedQuestion(value as SurveyAnswers)) return advancedQuestion.options.some((option) => option.code === answers.advanced);
  return true;
}
